import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { COUNTRIES, COUNTRY_BY_CODE } from "@/lib/countries";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n-dict";
import { LOCALE_HREFLANG } from "@/lib/i18n";
import { useLocale } from "@/hooks/useLocale";

export function CountrySelect({
  value,
  onChange,
  id = "country",
  invalid,
}: {
  value: string;
  onChange: (code: string) => void;
  id?: string;
  invalid?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const countries = useMemo(() => COUNTRIES, []);
  const t = useT();
  const locale = useLocale();
  const displayNames = useMemo(
    () => new Intl.DisplayNames([LOCALE_HREFLANG[locale]], { type: "region" }),
    [locale],
  );
  const countryName = (country: (typeof COUNTRIES)[number]) =>
    displayNames.of(country.code) ?? country.name;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid ? true : undefined}
          className={cn(
            "h-10 w-full justify-between bg-background font-normal",
            !value && "text-muted-foreground",
            invalid && "border-destructive",
          )}
        >
          {value
            ? countryName({ code: value, name: COUNTRY_BY_CODE[value] ?? value })
            : t("Select country")}
          <ChevronsUpDown className="size-4 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("Search country…")} />
          <CommandList>
            <CommandEmpty>{t("No country found.")}</CommandEmpty>
            <CommandGroup>
              {countries.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${countryName(country)} ${country.name} ${country.code}`}
                  onSelect={() => {
                    onChange(country.code);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("size-4", value === country.code ? "opacity-100" : "opacity-0")}
                    aria-hidden
                  />
                  {countryName(country)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
